/**
 * Companies Service
 * Handles company-related API operations
 */

import { apiClient } from '@/lib/api-client';
import { API_ENDPOINTS } from '@/config/api.config';
import { PaginatedResponse } from './leadsService';

// Types
export interface Company {
  id: string;
  name: string;
  slug: string;
  description?: string;
  industry?: string;

  // Platform flag - true only for OWLY platform company
  is_platform: boolean;

  // Market (legacy - single market)
  market?: string | null; // UUID del market
  market_name?: string; // Nombre del mercado

  // Markets (M2M - multiple markets)
  markets?: Array<{
    id: string;
    country_code: string;
    country_name: string;
    flag_emoji: string;
    currency_code: string | null;
  }>;
  market_ids?: string[]; // Write-only field for updates

  // Plan & Billing
  plan: 'free' | 'starter' | 'professional' | 'enterprise';
  status: 'active' | 'suspended' | 'cancelled' | 'trial';
  trial_ends_at?: string;
  subscription_ends_at?: string;

  // Limits
  max_users: number;
  max_projects: number;
  max_leads: number;
  max_storage_gb: number;
  
  // Contact
  email?: string;
  phone?: string;
  website?: string;
  
  // Address
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  zipcode?: string;
  
  // Branding
  logo?: string;
  primary_color?: string;
  secondary_color?: string;
  
  // Settings
  timezone: string;
  currency: string;
  language: string;

  // Effective settings (heredados del market si existe)
  effective_currency?: string;
  effective_currency_symbol?: string;
  effective_timezone?: string;

  // Billing
  billing_email?: string;
  tax_id?: string;
  legal_name?: string;

  // Metadata
  metadata?: any;
  features?: any;

  // Portal de Clientes - White Label Config
  portal_enabled?: boolean;
  portal_slug?: string;
  portal_logo_url?: string;
  portal_logo_dark_url?: string;
  portal_favicon_url?: string;
  portal_primary_color?: string;
  portal_secondary_color?: string;
  portal_document_label?: string;
  portal_welcome_message?: string;
  portal_features?: {
    payments_enabled?: boolean;
    construction_progress_enabled?: boolean;
    documents_enabled?: boolean;
    support_tickets_enabled?: boolean;
    benefits_enabled?: boolean;
    viewer_3d_enabled?: boolean;
  };

  // Counts
  user_count?: number;
  project_count?: number;

  // Sales metrics (aggregated from all projects)
  total_units?: number;
  sold_units?: number;
  reserved_units?: number;
  available_units?: number;

  // Timestamps
  created_at: string;
  createdAt?: string; // Alias for compatibility
  updated_at: string;
}

export interface CompanyStats {
  users: number;
  projects: number;
  max_users: number;
  max_projects: number;
  plan: string;
  status: string;
  can_add_user: boolean;
  can_add_project: boolean;
}

export interface UpgradePlanData {
  plan: 'free' | 'starter' | 'professional' | 'enterprise';
}

/**
 * Companies Service
 */
export const companiesService = {
  /**
   * Get all companies (superuser only)
   */
  async getCompanies(params?: Record<string, any>): Promise<PaginatedResponse<Company>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const endpoint = params 
      ? `${API_ENDPOINTS.COMPANIES.BASE}?${queryParams.toString()}`
      : API_ENDPOINTS.COMPANIES.BASE;
    
    return apiClient.get<PaginatedResponse<Company>>(endpoint);
  },

  /**
   * Get company by ID (or current company)
   */
  async getCompanyById(id: string): Promise<Company> {
    return apiClient.get<Company>(API_ENDPOINTS.COMPANIES.DETAIL(id));
  },

  /**
   * Create new company (superuser only)
   */
  async createCompany(data: Partial<Company>): Promise<Company> {
    return apiClient.post<Company>(API_ENDPOINTS.COMPANIES.BASE, data);
  },

  /**
   * Update company
   */
  async updateCompany(id: string, data: Partial<Company>): Promise<Company> {
    return apiClient.patch<Company>(API_ENDPOINTS.COMPANIES.DETAIL(id), data);
  },

  /**
   * Delete company (superuser only)
   */
  async deleteCompany(id: string): Promise<void> {
    return apiClient.delete<void>(API_ENDPOINTS.COMPANIES.DETAIL(id));
  },

  /**
   * Get company statistics
   */
  async getCompanyStats(id: string): Promise<CompanyStats> {
    return apiClient.get<CompanyStats>(API_ENDPOINTS.COMPANIES.STATS(id));
  },

  /**
   * Upgrade company plan
   */
  async upgradePlan(id: string, data: UpgradePlanData): Promise<Company> {
    return apiClient.post<Company>(API_ENDPOINTS.COMPANIES.UPGRADE_PLAN(id), data);
  },
};

